// Admin's landing screen (App.jsx routes an admin session's "/" here, a
// sales session to QueuePage instead — see this task's role-based routing
// requirement). Ledger-style: one ruled table, no sidebar, same shape as
// the sales queue — a queue is a queue, the two differ in what they list,
// not in layout language.

import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useApprovalQueue } from '../hooks/useApprovalQueue.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { formatMoney } from 'shared/money.js';
import { formatDateTime } from '../utils/formatDate.js';
import { useSortableList } from 'shared/hooks/useSortableList.js';
import { SortHeader } from 'shared/components/SortHeader/index.js';
import styles from './ApprovalQueuePage.module.css';

// key: the row field to sort on; numeric: compare as numbers, not strings —
// same shape as AllProformasPage.jsx's own SORT_COLUMNS.
const SORT_COLUMNS = {
  ref: { key: 'request_ref_no' },
  organization: { key: 'organization_name' },
  pricedBy: { key: 'priced_by_name' },
  submittedAt: { key: 'submitted_at' },
  total: { key: 'total', numeric: true },
};

export default function ApprovalQueuePage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const { data: quotations, isLoading, isError, error, refetch } = useApprovalQueue();
  // Default 'submittedAt' asc mirrors the server's own default order
  // (quotationRepository.js#listPendingApproval: submitted_at ASC,
  // oldest-waiting-first — left untouched by entry 130's sort-order change).
  const { sortCol, sortDir, handleSort, sortRows } = useSortableList(
    SORT_COLUMNS,
    'submittedAt',
    'asc'
  );
  const rows = sortRows(quotations || []);

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('approvalQueue.title')}</h1>
      <p className={styles.subtitle}>{t('approvalQueue.subtitle')}</p>

      {isLoading && <LoadingState variant="rows" rows={6} />}

      {isError && !isLoading && (
        <ErrorState messageId="approvalQueue.errorBody" error={error} onRetry={refetch} />
      )}

      {!isLoading && !isError && quotations && quotations.length === 0 && (
        <EmptyState titleId="approvalQueue.emptyTitle" bodyId="approvalQueue.emptyBody" />
      )}

      {!isLoading && !isError && quotations && quotations.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr className={styles.headerRow}>
              <SortHeader
                column="ref"
                label={t('approvalQueue.col.ref')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="organization"
                label={t('approvalQueue.col.organization')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="pricedBy"
                label={t('approvalQueue.col.pricedBy')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="submittedAt"
                label={t('approvalQueue.col.submittedAt')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
              />
              <SortHeader
                column="total"
                label={t('approvalQueue.col.total')}
                sortCol={sortCol}
                sortDir={sortDir}
                onSort={handleSort}
                align="right"
                thClassName={styles.totalHeader}
              />
              <th aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {rows.map((quotation) => (
              <tr
                key={quotation.id}
                className={styles.row}
                tabIndex={0}
                role="link"
                onClick={() => navigate(`/quotations/${quotation.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') navigate(`/quotations/${quotation.id}`);
                }}
              >
                <td className={`${styles.refCell} tabularNums`}>{quotation.request_ref_no}</td>
                <td>
                  {lang === 'am' && quotation.organization_name_am
                    ? quotation.organization_name_am
                    : quotation.organization_name}
                </td>
                <td>{quotation.priced_by_name || t('approvalDetail.pricedByUnknown')}</td>
                <td>{formatDateTime(quotation.submitted_at, lang) || '—'}</td>
                <td className={`${styles.totalCell} tabularNums`}>
                  {formatMoney(quotation.total)}
                </td>
                <td className={styles.openCell}>{t('approvalQueue.open')} →</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
